import { DEFAULT_HOUSEHOLD } from '../../data/household-defaults';
import type { Expense } from '../../domain/budget-summary';
import type { IsoDate, Period } from '../../domain/dates';
import type { Household } from '../../domain/household';
import type { Pantry } from '../../domain/pantry';
import type { Plan } from '../../domain/plan';
import type { Offer, OwnPrice } from '../../domain/price-book';
import type { ShoppingTrip } from '../../domain/shopping';
import { MESSAGES, type Language } from '../i18n/messages';
import { isEntryList, isHouseholdShape, isPantryShape, isStoredPlanShape } from './data-shape';

export const APP_DATA_VERSION = 1;

/**
 * DE: Hell oder dunkel: wie am Telefon, oder fest gewählt.
 * EN: Light or dark: as on the phone, or chosen.
 */
export type Appearance = 'system' | 'light' | 'dark';

export const APPEARANCES: readonly Appearance[] = ['system', 'light', 'dark'];

/**
 * DE: Gespeicherter Einkauf mit Kennung, Häkchen (`productId|storeId`) und „gekauft“.
 * EN: Stored trip with id, ticks (`productId|storeId`) and "bought".
 */
export interface StoredTrip extends ShoppingTrip {
  id: string;
  bought: boolean;
  checked: string[];
}

/**
 * DE: Gespeicherter Plan samt Einkäufen und Rücklagen (Notfallvorrat).
 * EN: Stored plan with its trips and reserves (backup kit).
 */
export interface StoredPlan {
  period: Period;
  seed: number;
  createdOn: IsoDate;
  reserved: { date: IsoDate; recipeId: string }[];
  plan: Plan;
  trips: StoredTrip[];
}

/**
 * DE: Alle Daten der App auf dem Gerät; `version` erlaubt spätere Umstellungen.
 * EN: All app data on the device; `version` allows later migrations.
 */
export interface AppData {
  version: typeof APP_DATA_VERSION;
  language: Language;
  /**
   * DE: Fehlt bei älteren Daten; dann gilt „wie am Telefon“.
   * EN: Missing in older data; then "as on the phone" applies.
   */
  appearance?: Appearance;
  /**
   * DE: Der Hinweis „Zum Home-Bildschirm“ wurde weggeklickt.
   * EN: The "add to home screen" hint was dismissed.
   */
  installHintDismissed?: boolean;
  household: Household;
  plans: StoredPlan[];
  offers: Offer[];
  ownPrices: OwnPrice[];
  expenses: Expense[];
  pantry: Pantry;
}

/**
 * DE: Sprache beim ersten Start: Deutsch bei deutscher Gerätesprache, sonst Ukrainisch.
 * EN: Language on first start: German for a German device language, otherwise Ukrainian.
 */
export function languageFromLocale(locale: string): Language {
  return locale.toLowerCase().startsWith('de') ? 'de' : 'uk';
}

/**
 * DE: Startdaten: vereinbarter Haushalt mit Personennamen in der gewählten Sprache.
 * EN: Starting data: the agreed household with member names in the chosen language.
 */
export function createDefaultData(language: Language): AppData {
  const names = MESSAGES[language].defaultMemberNames;
  const members = DEFAULT_HOUSEHOLD.members.map((member, index) => ({
    ...member,
    name: names[index] ?? member.name,
  }));
  return {
    version: APP_DATA_VERSION,
    language,
    household: { ...DEFAULT_HOUSEHOLD, members },
    plans: [],
    offers: [],
    ownPrices: [],
    expenses: [],
    pantry: {},
  };
}

/**
 * DE: JSON lesen; unlesbarer Text ergibt `undefined`.
 * EN: Read JSON; unreadable text gives `undefined`.
 */
export function readJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// DE: Später hinzugekommene Felder dürfen fehlen, aber keine fremden Werte haben.
// EN: Fields added later may be missing, but must not hold unknown values.
function optionalPartsKnown(value: Record<string, unknown>): boolean {
  const appearance =
    value.appearance === undefined || APPEARANCES.includes(value.appearance as Appearance);
  const hint =
    value.installHintDismissed === undefined || typeof value.installHintDismissed === 'boolean';
  return appearance && hint;
}

/**
 * DE: Gespeicherte Daten prüfen; unbekannte Formen ergeben `undefined`.
 * EN: Check stored data; unknown shapes give `undefined`.
 */
export function parseAppData(value: unknown): AppData | undefined {
  if (!isObject(value) || value.version !== APP_DATA_VERSION) return undefined;
  const knownLanguage = value.language === 'uk' || value.language === 'de';
  const parts =
    isHouseholdShape(value.household) &&
    Array.isArray(value.plans) &&
    value.plans.every(isStoredPlanShape) &&
    [value.offers, value.ownPrices, value.expenses].every(isEntryList) &&
    isPantryShape(value.pantry);
  return knownLanguage && optionalPartsKnown(value) && parts
    ? (value as unknown as AppData)
    : undefined;
}
